/**
 * variantResolution.ts — THE single source of truth for live-scan variant
 * resolution (consolidation PR, 2026-06-19).
 *
 * ── Why this module exists ────────────────────────────────────────────────
 * Before this module there were THREE parallel variant resolvers that could
 * disagree and drift apart by hand:
 *   1. `resolveTrustedVariant`  (dexLiveFrameAdapter.ts) — routing authority.
 *   2. `voteVariant`/`voteVariantGated` + tint positive-selection
 *      (variantSignals.ts) — a separate two-signal vote with its own colour map.
 *   3. `resolveFrameVariant`    (pokedexOcr.ts) — the resolver that actually
 *      TAGS the committed cell variant (the `[dex-scan] cell classify` field).
 * (1) and (3) were near-duplicate copies kept in sync manually — exactly the
 * drift risk that let PR #444 pass unit tests yet ship the XXL-on-cyan
 * zero-commit bug on device. This module is the ONE authority both now call,
 * so the routed variant and the committed cell variant can never diverge.
 *
 * ── Authority / dependency chain (IMPLEMENTED EXACTLY — user-decided) ──────
 * Inputs:  appStateVariant (frozen Nexus pill), chipText (pill OCR + confidence
 *          + hueAgrees corroboration), pageColourFamily (pre-OCR gate colour).
 * Resolve ONCE per frame, then apply to every cell committed from that frame.
 *
 *   1. COLOUR-IMPOSSIBILITY VETO — runs FIRST and ALWAYS, even on curated
 *      pages. A variant impossible for the current page-colour family can never
 *      be committed. The curated-page guard may still operate but can NEVER
 *      override the veto. (Veto-first.)
 *      EXCEPTION (added 2026-06-19): a DIRECT chip-text (pill OCR) read at or
 *      above CHIP_TEXT_VETO_OVERRIDE_THRESHOLD (0.95) is authoritative OVER the
 *      colour veto — it names the variant outright rather than guessing from a
 *      coarse page tint. The page-colour tracker (classifyPaletteRobust) was
 *      observed on device (2026-06-19) to mislabel boundary pages (purified
 *      page peakHue~156 → dex_cyan; lucky page peakHue~160 → dex_shadow_purple),
 *      which deadlocked every frame into variant_hold (OCR never ran). A
 *      high-confidence label read must not be killed by a boundary tint misread.
 *      Below that bar the veto still wins (a weak/ambiguous cross-family chip
 *      read is NOT trusted over the page physics).
 *   2. CHIP-TEXT (pill OCR) = PRIMARY positive selector, accepted ≥ 0.80
 *      (CHIP_TEXT_AUTHORITY_THRESHOLD). Disambiguates WITHIN a colour family
 *      (shiny vs lucky on golden; pokemon vs xxl vs xxs on cyan).
 *   3. TINT / HUE = VETO / CORROBORATION ONLY. It can corroborate chip-text
 *      (lowering the bar to CHIP_TEXT_CORROBORATED_THRESHOLD) or feed the
 *      colour veto, but it can NEVER independently SELECT a variant.
 *   4. STICKY last-known / app_state = fallback ONLY when not vetoed and
 *      chip-text is absent/ambiguous. NEVER silent-default to "pokemon" when a
 *      positive signal exists; the unfiltered-main default is handled upstream
 *      in the adapter (mainDexDefaulted), never here.
 *   5. BOTH-VETOED / impossible + unreadable chip → HOLD/SKIP the frame: return
 *      variant `null` so the caller commits NOTHING and waits for a frame where
 *      chip-text or colour agrees. Never write a wrong variant.
 *
 * ── Per-dex exceptions (the ONLY ones; every other dex is treated identically) ─
 *   - MANUAL_ONLY_VARIANTS (shundo, shinyLucky): NO scan path by design — there
 *     is no on-screen tab to resolve them to, so they are never selectable here.
 *     Rationale: manual-only dexes, documented in pogoPokedexVariantClassifier.
 *   - SPARSE_VARIANTS (shadow, purified, mega, gmax): resolvable and routable
 *     exactly like the others; their ONLY difference is downstream
 *     ("never infer missing" in the adapter / classifier). That per-dex
 *     difference is justified (curated pages skip dex numbers) and documented at
 *     SPARSE_VARIANTS — it is NOT a resolution difference.
 *
 * Pure, side-effect-free, no DOM — unit-testable with vitest or `npx tsx`.
 */

import {
  boundaryRecoveredVariant,
  colourFamilyVetoesVariant,
  isSparseVariant,
  soleVariantForColourFamily,
  COLOUR_RESOLVE_CHIP_REQUIRED,
  type PokedexVariant,
} from "./pogoPokedexVariantClassifier";

// ── P5 — centralised confidence config ─────────────────────────────────────

/**
 * The ONE place every variant-resolution confidence constant lives. Inlined
 * magic numbers (0.80 / 0.70 / 0.60 …) scattered across three resolvers were a
 * drift hazard — the per-cell copy sat at 0.9 while the adapter moved to 0.80,
 * which IS the #444 bug. Centralise + document the ordering invariant here and
 * assert it (see variantResolution.test.ts ordering test, P5).
 *
 * Ordering invariant (must hold; asserted in tests):
 *   CHIP_TEXT_OVER_MAIN_THRESHOLD (0.70)        — relaxed bar vs the stale-prone
 *                                                 unfiltered "pokemon" default
 *   ≤ CHIP_TEXT_CORROBORATED_THRESHOLD (0.80)   — hue-corroborated chip read
 *   == CHIP_TEXT_AUTHORITY_THRESHOLD (0.80)     — single-signal chip authority
 * and a positive pick of ANY confidence < an active colour VETO (veto wins).
 */
export const VARIANT_CONFIDENCE = {
  /**
   * Single-signal chip-text authority bar. A clear in-focus chip label at/above
   * this overrides a disagreeing (deliberately-selected) app_state tab.
   * #444 lowered this from 0.90 → 0.80 (chip-text primary); kept here as the one
   * source so the per-cell + routing copies cannot diverge again.
   */
  CHIP_TEXT_AUTHORITY_THRESHOLD: 0.8,
  /**
   * Corroborated bar: when the chip-text read is independently hue-corroborated
   * (`hueAgrees === true`), two agreeing signals clear this lower floor. Equal
   * to the authority bar today (P5b: the formerly-dead `hueAgrees` branch when
   * both were 0.80 is collapsed — see resolveVariant), kept as a named constant
   * so a future divergence is a one-line config change, not a logic edit.
   */
  CHIP_TEXT_CORROBORATED_THRESHOLD: 0.8,
  /**
   * Relaxed bar used ONLY when app_state is the unfiltered-main default
   * ("pokemon") — the value most likely to be stale relative to the camera. A
   * specific non-pokemon chip read at this floor wins over a bare "pokemon".
   */
  CHIP_TEXT_OVER_MAIN_THRESHOLD: 0.7,
  /**
   * Veto-override bar. A chip-text (pill OCR) read at or above this — a DIRECT
   * label read, not a coarse page-tint guess — is trusted OVER the colour
   * impossibility veto. The page-colour tracker (classifyPaletteRobust) was
   * observed on device (2026-06-19) to mislabel boundary pages (purified
   * peakHue~156 → dex_cyan; lucky peakHue~160 → dex_shadow_purple), vetoing a
   * correct 0.99 chip read and deadlocking every frame into variant_hold so OCR
   * never ran. A high-confidence label read must beat a boundary tint misread.
   * Set ABOVE the single-signal authority bar (0.80): only a very confident
   * direct read overrides the cross-family physics; weaker reads still defer to
   * the veto. Ordering invariant (asserted in tests):
   *   CHIP_TEXT_VETO_OVERRIDE_THRESHOLD (0.95) ≥ CHIP_TEXT_AUTHORITY_THRESHOLD.
   */
  CHIP_TEXT_VETO_OVERRIDE_THRESHOLD: 0.95,
} as const;

// Re-exported flat for ergonomic imports by the thin wrappers.
export const CHIP_TEXT_AUTHORITY_THRESHOLD =
  VARIANT_CONFIDENCE.CHIP_TEXT_AUTHORITY_THRESHOLD;
export const CHIP_TEXT_CORROBORATED_THRESHOLD =
  VARIANT_CONFIDENCE.CHIP_TEXT_CORROBORATED_THRESHOLD;
export const CHIP_TEXT_OVER_MAIN_THRESHOLD =
  VARIANT_CONFIDENCE.CHIP_TEXT_OVER_MAIN_THRESHOLD;
export const CHIP_TEXT_VETO_OVERRIDE_THRESHOLD =
  VARIANT_CONFIDENCE.CHIP_TEXT_VETO_OVERRIDE_THRESHOLD;

// ── Inputs / outputs ────────────────────────────────────────────────────────

export interface ChipTextRead {
  /** Variant the chip-text (pill OCR) named for this frame. */
  variant: PokedexVariant;
  /** 0..1 OCR confidence. */
  confidence: number;
  /**
   * True when an independent hue check corroborated the chip-text variant.
   * CORROBORATION ONLY — never selects; only lowers the chip-text authority bar.
   */
  hueAgrees?: boolean;
  /**
   * Fine-grained provenance of the read from activePillClassifier:
   * "active-pill-text" / "active-pill-text-strong" / etc, preserved through the
   * caller. A read whose source is a DIRECT active-pill-strip label
   * is the apply-back authority: it OCRd the literal active pill capsule, not a
   * coarse page tint. Such a read may override the colour-impossibility veto at
   * the AUTHORITY bar of 0.80 rather than the global VETO_OVERRIDE bar of 0.95
   * — see the active-pill veto-override branch in resolveVariant. Any other or
   * absent source keeps the 0.95 bar.
   */
  source?: string;
}

export type VariantResolutionOutcome =
  /** chip-text positively selected the variant (override of app_state). */
  | "chip-text"
  /**
   * An UNAMBIGUOUS page colour (a family that admits EXACTLY ONE variant)
   * positively resolved the variant when app_state was vetoed and no chip-text
   * contradicted it. Like "chip-text" it is a positive resolution with
   * `variantOverride: true`.
   */
  | "colour-resolve"
  /**
   * A MULTI-variant page colour (e.g. dex_pink → {gmax, mega}) resolved via the
   * caller's WIDE colour-narrowed pill re-read when app_state was vetoed and the
   * active-pill read failed or named a colour-impossible variant. The narrowed
   * re-read named a variant ALLOWED for the family, so the page colour + the
   * family-filtered keyword together disambiguate it. Positive resolution with
   * `variantOverride: true`.
   */
  | "colour-narrowed"
  /**
   * A single-variant curated dex misread as a tint-ADJACENT multi-variant family
   * (device: the Purified mint wash classifies as dex_cyan) was recovered because
   * app_state names that adjacent family's sole variant — i.e. the user actively
   * selected the filter. Positive resolution with `variantOverride: true`. See
   * pogoPokedexVariantClassifier.boundaryRecoveredVariant for the evidence gate.
   */
  | "boundary-recover"
  /** app_state was accepted (chip absent/ambiguous, app_state not vetoed). */
  | "app-state"
  /**
   * HOLD/SKIP: nothing safe to commit this frame — app_state was vetoed and
   * chip-text could not supply an allowed variant. Caller commits NOTHING.
   */
  | "hold";

export interface VariantResolution {
  /** Resolved variant, or null to HOLD/SKIP the frame (commit nothing). */
  variant: PokedexVariant | null;
  /** True when chip-text overrode app_state (logged as `variantOverride`). */
  variantOverride: boolean;
  /** Provenance for diagnostics / device traces. */
  outcome: VariantResolutionOutcome;
}

export interface ResolveVariantInput {
  /** The frozen Nexus-side pill tab (sticky source of truth when not vetoed). */
  appStateVariant: PokedexVariant;
  /** Chip-text (pill OCR) read for this frame, or null when unreadable. */
  chipText?: ChipTextRead | null;
  /**
   * Pre-OCR gate's page-colour family for this frame (e.g. "dex_cyan").
   * VETO/CORROBORATION ONLY: it can reject an impossible variant but never
   * positively select one. Undefined / non-dex families make the veto inert.
   */
  pageColourFamily?: string | null;
  /**
   * G-Max stability fix (telemetry 2026-06-21). The caller's WIDE colour-narrowed
   * pill re-read result for THIS frame — the variant a family-filtered pill OCR
   * matched, or null when nothing matched. Consulted ONLY for a MULTI-variant
   * colour family when app_state is colour-vetoed and the active-pill read could
   * not supply an allowed variant (the device repro: a G-Max page reads
   * `dex_pink` while the frozen Nexus pill is `perfect`, the active-pill OCR
   * intermittently misreads `xxs`/fails, and every such frame HELD even though the
   * colour-narrowed re-read recovered `gmax`). Re-validated against the colour
   * veto here so an impossible token can never resolve. Single-variant families
   * still resolve via `soleVariantForColourFamily` and ignore this input.
   */
  colourNarrowedVariant?: PokedexVariant | null;
}

// ── The ONE authority chain ─────────────────────────────────────────────────

/**
 * Resolve the single authoritative variant for a frame. See the module header
 * for the exact chain. Pure; the caller (routing AND per-cell commit) threads
 * the SAME inputs so the committed cell variant always equals the routed one.
 *
 * `null` variant is the HOLD/SKIP signal — the caller must commit nothing.
 */
export function resolveVariant(input: ResolveVariantInput): VariantResolution {
  const {
    appStateVariant,
    chipText = null,
    pageColourFamily = null,
    colourNarrowedVariant = null,
  } = input;

  const chipVariant = chipText?.variant ?? null;
  const appStateVetoed = colourFamilyVetoesVariant(pageColourFamily, appStateVariant);
  const chipVetoed =
    chipVariant != null && colourFamilyVetoesVariant(pageColourFamily, chipVariant);

  // ── 1 (exception). HIGH-CONFIDENCE CHIP-TEXT BEATS THE COLOUR VETO. ──
  // A direct pill-OCR label at/above CHIP_TEXT_VETO_OVERRIDE_THRESHOLD is more
  // trustworthy than the coarse page-tint family, which mislabels boundary
  // pages (device 2026-06-19: purified peakHue~156 → dex_cyan, lucky
  // peakHue~160 → dex_shadow_purple). When such a read DISAGREES with the frozen
  // app_state filter yet names a valid variant, we accept it EVEN IF the colour
  // family would veto it — the disagreement is the signal that the page tint is
  // a boundary misread, so the direct label wins. This runs before the veto so
  // the deadlock can't recur. Only `chipText` (pill OCR) reaches here; hue-source
  // chips are nulled by the caller (resolveFrameVariant), so a hue guess can
  // never select.
  //
  // Crucially this fires ONLY on disagreement (chipVariant !== appStateVariant).
  // When chip-text AGREES with an already-impossible frozen filter (e.g. frozen
  // shiny + chip shiny on a cyan page), there is no contradicting evidence that
  // the tint is wrong — both signals say the same impossible thing — so the veto
  // still wins and the frame HOLDs (see resolveVariant HOLD case below).
  if (
    chipText != null &&
    chipVariant != null &&
    chipVariant !== appStateVariant &&
    chipText.confidence >= VARIANT_CONFIDENCE.CHIP_TEXT_VETO_OVERRIDE_THRESHOLD
  ) {
    return { variant: chipVariant, variantOverride: true, outcome: "chip-text" };
  }

  // ── 1b. DIRECT ACTIVE-PILL LABEL BEATS THE COLOUR VETO AT THE AUTHORITY BAR. ──
  // The colour-impossibility veto guards against COARSE page-tint guesses, and
  // the 0.95 bar above exists to distrust weak / hue-sourced / cross-family chip
  // reads. But a read whose fine source is the DIRECT active-pill strip
  // ("active-pill-text" / "active-pill-text-strong") is a different class of
  // evidence: the OCR literally read the active pill capsule — the on-screen
  // control that tells the scan WHICH variant to apply back. That is the
  // apply-back authority, strictly stronger than a palette classifier that is
  // known to mislabel washed pastel pages under display warming / True Tone
  // (device 2026-06-28: a Perfect peach page classified frame-to-frame as
  // dex_mint / uniform_unknown / nondex_mixed before settling on dex_peach).
  // When such a DIRECT read DISAGREES with the frozen app_state and clears the
  // AUTHORITY bar (0.80), trust it OVER the veto — the disagreement is itself the
  // signal that the page tint is a boundary/warmth misread.
  //
  // SCOPE — why this does not loosen the global veto: it fires ONLY for the two
  // direct-strip sources. A chip with no `source` (e.g. the line-124
  // write-accuracy guard: a generic purified@0.85 on a cyan page) does NOT take
  // this path and is still held by the colour veto, so the 0.95 bar and the
  // write-accuracy invariant are untouched for every non-active-pill read.
  const isDirectActivePillRead =
    chipText != null &&
    (chipText.source === "active-pill-text" ||
      chipText.source === "active-pill-text-strong");
  if (
    chipText != null &&
    chipVariant != null &&
    chipVariant !== appStateVariant &&
    isDirectActivePillRead &&
    chipText.confidence >= VARIANT_CONFIDENCE.CHIP_TEXT_AUTHORITY_THRESHOLD
  ) {
    return { variant: chipVariant, variantOverride: true, outcome: "chip-text" };
  }

  // ── 2/3. Chip-text PRIMARY positive selector (subject to the colour veto). ──
  // A chip-text read that disagrees with app_state may override it when it
  // clears the threshold ladder AND is not colour-impossible. The colour veto
  // (step 1) is enforced inline via `chipVetoed`: a vetoed chip can never win,
  // so the impossibility veto runs first and always. The curated-page guard
  // (sparse app_state) is applied ONLY AFTER the veto has had its say, so it can
  // never resurrect an impossible app_state variant.
  if (chipText != null && chipVariant != null && chipVariant !== appStateVariant && !chipVetoed) {
    // P1 curated-page guard: when app_state is a deliberately-selected sparse
    // page (mega/gmax/shadow/purified), a MAIN-dex chip read may not override it
    // — only a sparse chip read can. This prevents a stray inactive main-dex
    // pill read leaking phantom species into a curated grid. The guard is
    // SUBORDINATE to the veto: if app_state is itself colour-vetoed we skip the
    // guard and let chip-text re-resolve below.
    const curatedGuardBlocks =
      isSparseVariant(appStateVariant) &&
      !isSparseVariant(chipVariant) &&
      !appStateVetoed;
    if (!curatedGuardBlocks) {
      const threshold =
        appStateVariant === "pokemon" && chipVariant !== "pokemon"
          ? VARIANT_CONFIDENCE.CHIP_TEXT_OVER_MAIN_THRESHOLD
          : chipText.hueAgrees === true
            ? VARIANT_CONFIDENCE.CHIP_TEXT_CORROBORATED_THRESHOLD
            : VARIANT_CONFIDENCE.CHIP_TEXT_AUTHORITY_THRESHOLD;
      if (chipText.confidence >= threshold) {
        return { variant: chipVariant, variantOverride: true, outcome: "chip-text" };
      }
    }
  }

  // ── 1 (continued). VETO on the frozen app_state variant. ──
  // If app_state is impossible for the page colour, it can never be committed.
  // When chip-text named an ALLOWED variant (even below the override bar) we
  // re-resolve to it — the frozen filter is demonstrably wrong for the page
  // (XXL-on-cyan: frozen "shiny" impossible on cyan, chip-text "xxl" valid).
  // Colour never positively picks; chip-text supplies the variant.
  if (appStateVetoed) {
    if (chipText != null && chipVariant != null && chipVariant !== appStateVariant && !chipVetoed) {
      return { variant: chipVariant, variantOverride: true, outcome: "chip-text" };
    }
    // ── COLOUR-RESOLVE: an UNAMBIGUOUS page colour (single allowed variant) may
    // positively resolve when the frozen app_state is impossible and NOTHING
    // contradicts the page colour. Fires only for single-variant families
    // (peach→perfect, mint→purified, shadow_purple→shadow); multi-variant
    // families (cyan, golden, pink) stay HOLD because the page tint alone cannot
    // disambiguate them. The single-variant set is derived from the veto map's
    // cardinality (soleVariantForColourFamily), so it tracks the map with no
    // hardcoded literals. This is NOT a new trusted signal: dex_peach is the SAME
    // classification the veto already trusts to BLOCK "pokemon" here — we only let
    // an already-trusted-enough page colour resolve instead of veto-only.
    const soleVariant = soleVariantForColourFamily(pageColourFamily);
    if (
      soleVariant != null &&
      // No chip-text contradicting the page colour: either no chip at all, or the
      // chip names the SAME sole variant (agreement). A chip naming a DIFFERENT
      // variant is a contradiction → do NOT colour-resolve; fall through to hold.
      (chipVariant == null || chipVariant === soleVariant) &&
      // 2026-08-30 audit decision: for COLOUR_RESOLVE_CHIP_REQUIRED variants
      // (perfect), "nothing contradicts" is not enough - the chip must AGREE.
      // An unreadable pill on a warm wash holds instead of filling the
      // Perfect dex; see the set's doc for the full rationale.
      (!COLOUR_RESOLVE_CHIP_REQUIRED.has(soleVariant) || chipVariant === soleVariant)
    ) {
      return { variant: soleVariant, variantOverride: true, outcome: "colour-resolve" };
    }
    // ── COLOUR-NARROWED: a MULTI-variant page colour (soleVariant == null, e.g.
    // dex_pink → {gmax, mega}) cannot be resolved by the page tint alone, but the
    // caller's WIDE colour-narrowed pill re-read (a family-FILTERED pill OCR) may
    // have matched a variant ALLOWED for the family. When the frozen app_state is
    // impossible and the active-pill read failed or named a colour-impossible
    // variant, that narrowed match is the only positive signal — accept it,
    // re-validated against the veto so an impossible token can never resolve.
    // Device repro (telemetry 2026-06-21): a G-Max page reads dex_pink while the
    // frozen Nexus pill is "perfect" (vetoed) and the active-pill OCR intermittently
    // misreads "xxs"/fails; every such frame HELD even though the colour-narrowed
    // re-read recovered "gmax". Guards mirror colour-resolve: the narrowed variant
    // must not itself be vetoed, and any chip-text must agree (a chip naming a
    // DIFFERENT variant is a contradiction → fall through to hold).
    if (
      colourNarrowedVariant != null &&
      !colourFamilyVetoesVariant(pageColourFamily, colourNarrowedVariant) &&
      (chipVariant == null || chipVariant === colourNarrowedVariant)
    ) {
      return {
        variant: colourNarrowedVariant,
        variantOverride: true,
        outcome: "colour-narrowed",
      };
    }
    // ── BOUNDARY-RECOVER: a single-variant curated dex misread as a tint-adjacent
    // multi-variant family. The device repro: the Purified page's mint wash reads
    // B-dominant and classifies as dex_cyan (a {pokemon,xxl,xxs} family), so
    // purified is vetoed, the active-pill read fails, and the colour-narrowed
    // re-read can NEVER match "purified" (not a cyan token) — every frame HELD
    // forever (134 dex_cyan locks, 0 purified). When app_state NAMES that adjacent
    // family's sole variant (the user actively selected the Purified filter, which
    // only happens while viewing that dex) we recover it. Gated to documented
    // adjacencies + a non-contradicting chip; a stale non-sole app_state (e.g.
    // shiny) does not match and still HOLDs.
    const boundary = boundaryRecoveredVariant(pageColourFamily, appStateVariant);
    if (boundary != null && (chipVariant == null || chipVariant === boundary)) {
      return { variant: boundary, variantOverride: true, outcome: "boundary-recover" };
    }
    // ── 5. BOTH-VETOED / impossible app_state + no allowed chip → HOLD/SKIP. ──
    // app_state is impossible and chip-text could not supply an allowed variant
    // (absent, agreeing-with-the-impossible-app_state, or itself vetoed).
    // Commit nothing — never write the wrong variant.
    return { variant: null, variantOverride: false, outcome: "hold" };
  }

  // ── 4. Sticky fallback: app_state is allowed for the page → accept it. ──
  return { variant: appStateVariant, variantOverride: false, outcome: "app-state" };
}

// ── P3 — session-scoped hue-gate state (no module globals) ──────────────────

/**
 * Per-session chip-text hue-gate state. Previously variantSignals held this as
 * MODULE-LEVEL singletons (`_frameCounter` etc.), so two sequential live-scan
 * sessions leaked state into each other. It now lives in a session-owned object
 * created by `createChipTextGate`, so each session is isolated (test: two
 * sequential gates do not leak — variantResolution.test.ts).
 *
 * Purpose (unchanged from Build 8): when chip-text fired at high confidence
 * within HUE_GATE_WINDOW frames, suppress a tint-only fallback so a colliding
 * page-chrome hue cannot overwrite a confirmed chip read. Tint is
 * corroboration/veto-only in the unified chain, so this is now belt-and-braces.
 */
export const HUE_GATE_WINDOW = 10;
export const HUE_GATE_MIN_CONFIDENCE = 0.95;

export interface ChipTextGate {
  /** Advance the per-session frame counter. Call once per OCR frame. */
  tickFrame(): void;
  /** Record a chip-text read for gating. Call whenever tab/pill OCR fires. */
  updateChipTextCache(variant: PokedexVariant | null, confidence: number): void;
  /** True when a high-confidence chip read fired within the window. */
  isHueGated(): boolean;
  /** The cached chip-text variant when gated, else null. */
  getCachedChipTextVariant(): PokedexVariant | null;
  /** Reset all gate state (between sessions / for tests). */
  reset(): void;
}

export function createChipTextGate(): ChipTextGate {
  let frameCounter = 0;
  let lastChipTextFrame = -Infinity;
  let lastChipTextVariant: PokedexVariant | null = null;
  let lastChipTextConfidence = 0;

  function isHueGated(): boolean {
    return (
      lastChipTextVariant != null &&
      frameCounter - lastChipTextFrame < HUE_GATE_WINDOW &&
      lastChipTextConfidence >= HUE_GATE_MIN_CONFIDENCE
    );
  }

  return {
    tickFrame() {
      frameCounter += 1;
    },
    updateChipTextCache(variant, confidence) {
      if (variant != null && confidence >= HUE_GATE_MIN_CONFIDENCE) {
        lastChipTextFrame = frameCounter;
        lastChipTextVariant = variant;
        lastChipTextConfidence = confidence;
      }
    },
    isHueGated,
    getCachedChipTextVariant() {
      return isHueGated() ? lastChipTextVariant : null;
    },
    reset() {
      frameCounter = 0;
      lastChipTextFrame = -Infinity;
      lastChipTextVariant = null;
      lastChipTextConfidence = 0;
    },
  };
}
